import React, {useState, useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import Separator from '../../../../../components/Simple/Sepperator';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import {useTranslation} from 'react-i18next';
import SMCTable from '../../../../../components/core/SMCTable/SMCTable';
import ButtonContainer from '../../../../../components/Simple/ButtonContainer';
import {useBoolean, useSMCSnackbar} from '../../../../../utils/hooks';
import SMCButton from '../../../../../components/Simple/SMCButton';
import SMCLink from '../../../../../components/Simple/SMCLink';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";
import GatewayLocalUserEditorModal from "../../../../PopupPages/GatewayLocalUserEditorModal";

const pageSchema = GatewayGetSchema.pages.localUsers;
const pageId = pageSchema.pageId;

function BaseLocalUsers({
                                pageData,
                                setObjectData,
                                pageDisabled
                            }) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();

    const managedInSmp   = pageData[pageSchema.manageInSmp];
    const localUsers = pageData[pageSchema.localUsers];

    const setPageData = useCallback((paramName, newValue) => {
        if (!pageDisabled) {
            setObjectData([pageId, paramName], newValue);
        }
    }, [pageDisabled, setObjectData]);

    const [isEditorOpen, openEditor, closeEditor] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);
    const [editedObject, setEditedObject] = useState(null);
    const [editedObjectIndex, setEditedObjectIndex] = useState(null);

    const deleteSelectedUsers = useCallback(() => {
        if (localUsers && selectedObjects.length > 0) {
            const newUserObject = [...localUsers];

            for (let i = 0; i < newUserObject.length; i++) {
                const objName = newUserObject[i][pageSchema.localUsersSchema.username];
                if (selectedObjects.indexOf(objName) !== -1) {
                    newUserObject.splice(i, 1);
                    i--;
                }
            }
            setSelectedObjects([]);
            setPageData(pageSchema.localUsers, newUserObject);
        }
    }, [localUsers, selectedObjects, setPageData]);

    const usersEditorOnCancel = useCallback(() => {
        // Set edit object and edit object key to null, to signify
        setEditedObject(null);
        setEditedObjectIndex(null);
        closeEditor();
    }, [closeEditor]);

    const newUserCallback = useCallback((newUserObject) => {
        if (localUsers && localUsers.find(user => user[pageSchema.localUsersSchema.username] === newUserObject.username)) {
            enqueueErrorSnackbar('The ID "' + newUserObject.username + '" is already in use.', {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
        } else {
            const userCopy = localUsers ? [...localUsers] : [];
            userCopy.push({
                [pageSchema.localUsersSchema.username]: newUserObject.username,
                [pageSchema.localUsersSchema.password]: newUserObject.password,
                [pageSchema.localUsersSchema.email]: newUserObject.email,
                [pageSchema.localUsersSchema.phoneNumber]: newUserObject.phoneNumber,
                [pageSchema.localUsersSchema.comments]: newUserObject.comments,
                [pageSchema.localUsersSchema.isRemoteAccessUser]: newUserObject.isRemoteAccessUser,
            });
            setPageData(pageSchema.localUsers, userCopy);
            closeEditor();
        }
    }, [localUsers, closeEditor, enqueueErrorSnackbar, setPageData]);

    const editUserCallback = useCallback((newUserObject) => {
        // If name changed, check if that name exists already
        if (
            (localUsers[editedObjectIndex][pageSchema.localUsersSchema.username] !== newUserObject.username) &&
            (localUsers && localUsers.find(localUser => localUser[pageSchema.localUsersSchema.username] === newUserObject.username))
        ) {
            enqueueErrorSnackbar('The ID "' + newUserObject.username + '" is already in use.', {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
        } else {
            const userCopy = localUsers ? [...localUsers] : [];
            userCopy[editedObjectIndex] = {
                [pageSchema.localUsersSchema.username]: newUserObject.username,
                [pageSchema.localUsersSchema.password]: newUserObject.password,
                [pageSchema.localUsersSchema.email]: newUserObject.email,
                [pageSchema.localUsersSchema.phoneNumber]: newUserObject.phoneNumber,
                [pageSchema.localUsersSchema.comments]: newUserObject.comments,
                [pageSchema.localUsersSchema.isRemoteAccessUser]: newUserObject.isRemoteAccessUser,
            };
            setPageData(pageSchema.localUsers, userCopy);
            setEditedObject(null);
            setEditedObjectIndex(null);
            closeEditor();
        }
    }, [localUsers, closeEditor, editedObjectIndex, enqueueErrorSnackbar, setPageData]);

    const UsernameCell = React.memo(({value, object, index}) => {
        return (
            <SMCLink
                inline
                disabled={pageDisabled || !managedInSmp}
                onClick={() => {
                    setEditedObject(object);
                    setEditedObjectIndex(index);
                    openEditor();
                }}
            >
                { value }
            </SMCLink>
        );
    });

    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={managedInSmp}
                onChange={checked => setPageData(pageSchema.manageInSmp,checked)}
                label={t("SMP.PLAN.MANAGE_IN_SMP")}
            />
            <Separator isMainSeparator/>
            <PlanEditorInnerContainer title={t('SMP.PLAN.DS.LOCAL_USERS.TITLE')} managedInSmp={managedInSmp}>
                <ButtonContainer>
                    <SMCButton variant="ghost" disabled={pageDisabled || !managedInSmp} icon="new" onClick={openEditor}>{t('SMC_GEN_NEW')}</SMCButton>
                    <SMCButton variant="ghost" disabled={pageDisabled || !managedInSmp} icon="delete" onClick={deleteSelectedUsers}>{t('SMC_GEN_DELETE')}</SMCButton>
                </ButtonContainer>
                <SMCTable
                    columns={[
                        {id: 'username', accessor: pageSchema.localUsersSchema.username, header: t('SMP.NAME'),       width: 175, Cell: UsernameCell }
                    ]}
                    isRowSelectable={true}
                    rowIdSelector={(obj) => (obj[pageSchema.localUsersSchema.username])}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    content={localUsers}
                    width={500}
                    maxHeight={500}
                />
                <GatewayLocalUserEditorModal
                    isOpen={isEditorOpen}
                    onSuccess={editedObject ? editUserCallback : newUserCallback}
                    onCancel={usersEditorOnCancel}
                    defaultObject={editedObject}
                />
            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}

export default BaseLocalUsers;